/**
 * v5.9 Addition 2 — Field Mapping / Approval screen.
 *
 * Two flows share one page:
 *  a) Pre-deal mapping — reached from Approvals ▸ Accept. On confirm, creates
 *     the Deal, migrates staged attachments, and redirects to Deal Detail.
 *  b) Existing-deal mapping — reached from Deal Detail ▸ Upload Deck &
 *     Auto-Populate. On confirm, writes chosen values back to the existing
 *     Deal.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTerm } from "@/lib/terminology.jsx";
import { DEAL_SOURCE_OPTIONS, matchSourceOption } from "@/lib/dealSourceOptions";
import {
  ArrowLeft, Sparkles, ExternalLink, Trash2, CheckCircle2,
  RefreshCw, User, Plus, Loader2, AlertTriangle,
} from "lucide-react";

const POLL_INTERVAL_MS = 2500;

export default function FieldMapping() {
  const { runId } = useParams();
  const nav = useNavigate();
  const term = useTerm();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});             // deal fields
  const [founderBlocks, setFounderBlocks] = useState([]); // per-founder maps
  const [stageId, setStageId] = useState("");
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/agent-runs/${runId}`);
    setData(data);
    return data;
  }, [runId]);

  // Initial + poll while queued/running
  useEffect(() => {
    let alive = true;
    let tid;
    const tick = async () => {
      try {
        const d = await load();
        if (!alive) return;
        if (d.run.status === "queued" || d.run.status === "running") {
          tid = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (e) {
        toast.error("Failed to load run");
      }
    };
    tick();
    return () => { alive = false; if (tid) clearTimeout(tid); };
  }, [load]);

  // Seed form state once the run completes
  useEffect(() => {
    if (!data || data.run.status !== "completed") return;
    // Only initialise once (when values are still empty)
    if (Object.keys(values).length > 0 || founderBlocks.length > 0) return;

    const dealMap = {};
    const blocks = {};
    for (const s of data.suggestions) {
      if (s.founder_index == null) {
        // Prefer higher confidence when duplicates land
        const prev = dealMap[s.field_key];
        if (!prev || confidenceRank(s.confidence) > confidenceRank(prev.confidence)) {
          // CW6.2.7: normalize Source into shared dropdown value (empty if no match)
          let value = s.suggested_value;
          if (s.field_key === "source") {
            value = matchSourceOption(value);
          }
          dealMap[s.field_key] = { value, meta: s };
        }
      } else {
        const idx = s.founder_index;
        if (!blocks[idx]) blocks[idx] = {};
        blocks[idx][s.field_key] = { value: s.suggested_value, meta: s };
      }
    }
    setValues(dealMap);
    const founderList = Object.keys(blocks).length
      ? Object.keys(blocks).sort((a, b) => Number(a) - Number(b)).map((k) => blocks[k])
      : [{}]; // always show one founder block
    setFounderBlocks(founderList);

    // Preselect first stage
    if (data.stages && data.stages.length > 0) setStageId(data.stages[0].stage_id);
  }, [data, values, founderBlocks.length]);

  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/agent-runs/${runId}/retry`);
      toast.success("Retrying extraction");
      // Reset form state so seeding runs again after retry
      setValues({});
      setFounderBlocks([]);
      const d = await load();
      if (d.run.status === "queued" || d.run.status === "running") {
        setTimeout(async function loop() {
          const d2 = await load();
          if (d2.run.status === "queued" || d2.run.status === "running") {
            setTimeout(loop, POLL_INTERVAL_MS);
          }
        }, POLL_INTERVAL_MS);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const setDealValue = (key, val) => {
    setValues((v) => ({ ...v, [key]: { ...(v[key] || {}), value: val } }));
  };
  const clearDealValue = (key) => {
    setValues((v) => { const n = { ...v }; delete n[key]; return n; });
  };
  const setFounderValue = (idx, key, val) => {
    setFounderBlocks((prev) => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || {}), [key]: { ...(next[idx]?.[key] || {}), value: val } };
      return next;
    });
  };
  const clearFounderValue = (idx, key) => {
    setFounderBlocks((prev) => {
      const next = [...prev];
      if (next[idx]) { const n = { ...next[idx] }; delete n[key]; next[idx] = n; }
      return next;
    });
  };
  const addFounderBlock = () => setFounderBlocks((b) => [...b, {}]);
  const removeFounderBlock = (idx) => {
    setFounderBlocks((b) => b.filter((_, i) => i !== idx));
  };

  // Validation — must run BEFORE any early return to satisfy rules-of-hooks
  const requiredMissing = useMemo(() => {
    if (!data || data.run?.status !== "completed") return [];
    const missing = [];
    for (const f of data.deal_fields) {
      if (!f.effective_required) continue;
      const key = f.is_custom ? f.field_definition_id : f.field_key;
      const v = values[key]?.value;
      if (!v || String(v).trim() === "") {
        missing.push({ scope: "deal", label: f.effective_label });
      }
    }
    // Ensure at least one founder with first_name (pre-deal only)
    if (data.run?.source_type === "email_approval") {
      const hasPrimaryFounder = founderBlocks.some((b) => (b?.first_name?.value || "").trim());
      if (!hasPrimaryFounder) {
        missing.push({ scope: "founder", label: `Primary ${term("founder").toLowerCase()} first name` });
      }
    }
    return missing;
  }, [data, values, founderBlocks, term]);

  if (!data) return <div className="p-10 text-[var(--cw-muted)]">Loading run…</div>;

  const isPreDeal = data.run.source_type === "email_approval";
  const isRunning = data.run.status === "queued" || data.run.status === "running";
  const isFailed = data.run.status === "failed";
  const isCompleted = data.run.status === "completed";

  const submit = async () => {
    if (requiredMissing.length > 0) {
      toast.error(`Missing: ${requiredMissing.map((m) => m.label).join(", ")}`);
      return;
    }
    const payloadValues = [];
    for (const [k, v] of Object.entries(values)) {
      if (v?.value != null && v.value !== "") {
        payloadValues.push({ field_key: k, value: String(v.value), founder_index: null });
      }
    }
    founderBlocks.forEach((blk, idx) => {
      for (const [k, v] of Object.entries(blk || {})) {
        if (v?.value != null && v.value !== "") {
          payloadValues.push({ field_key: k, value: String(v.value), founder_index: idx });
        }
      }
    });

    setSaving(true);
    try {
      if (isPreDeal) {
        const { data: res } = await api.post(`/agent-runs/${runId}/create-deal`, {
          values: payloadValues, stage_id: stageId || null,
        });
        toast.success(`${term("Deal")} created`);
        nav(`/deals/${res.deal_id}`);
      } else {
        const { data: res } = await api.post(`/agent-runs/${runId}/apply`, {
          values: payloadValues,
        });
        toast.success(`${res.applied} fields applied`);
        nav(`/deals/${res.deal_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-[1200px] mx-auto">
      <button onClick={() => nav(-1)}
        className="text-sm inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="back-btn">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="cw-caption uppercase tracking-widest">
            {isPreDeal ? "New " + term("Deal") + " · Field mapping" : "Re-extract · Existing " + term("deal").toLowerCase()}
          </div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <Sparkles size={22} />
            {data.approval?.candidate_company_name
              || data.run.resulting_deal_id
              || "Extraction review"}
          </h1>
          <div className="text-sm text-[var(--cw-muted)] mt-1">
            {data.pipeline?.pipeline_name} · Extracted by AI · You approve every value
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isRunning && (
            <span className="cw-badge inline-flex items-center gap-1"
              style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}
              data-testid="run-status">
              <Loader2 size={12} className="animate-spin" /> Extracting…
            </span>
          )}
          {isCompleted && (
            <span className="cw-badge" style={{ background: "rgba(44,154,82,0.12)", color: "#1F6E3A" }}>
              <CheckCircle2 size={12} /> Extraction complete
            </span>
          )}
          {isFailed && (
            <button onClick={retry} disabled={retrying}
              className="cw-btn-primary text-sm inline-flex items-center gap-1"
              data-testid="retry-btn">
              <RefreshCw size={12} className={retrying ? "animate-spin" : ""} /> Retry
            </button>
          )}
        </div>
      </div>

      {isFailed && (
        <div className="mt-4 rounded-md p-3"
          style={{ background: "rgba(214,69,69,0.10)", color: "var(--cw-error)" }}
          data-testid="run-error">
          <AlertTriangle size={14} className="inline mr-1" />
          Extraction failed: {data.run.error || "unknown error"}
        </div>
      )}

      {isRunning && (
        <div className="mt-8 cw-card p-10 text-center">
          <Loader2 className="animate-spin mx-auto text-[var(--cw-primary)]" size={30} />
          <div className="cw-h3 mt-3">Reading the deck…</div>
          <div className="text-sm text-[var(--cw-muted)] mt-1">
            Claude is extracting values. This usually takes 10–30 seconds.
          </div>
        </div>
      )}

      {isCompleted && (
        <>
          {/* Deal fields */}
          <FieldGroup
            title={term("Deal") + " details"}
            fields={data.deal_fields}
            values={values}
            onChange={setDealValue}
            onClear={clearDealValue}
            testPrefix="deal"
          />

          {/* Founder blocks */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="cw-h3 inline-flex items-center gap-2">
                <User size={18} /> {term("Founders")}
              </h3>
              <button onClick={addFounderBlock}
                className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="add-founder-btn">
                <Plus size={12} /> Add {term("Founder").toLowerCase()}
              </button>
            </div>
            <div className="space-y-4">
              {founderBlocks.map((blk, idx) => (
                <div key={idx} className="cw-card p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="cw-caption">
                      {term("Founder")} #{idx + 1}{idx === 0 && " · primary"}
                    </div>
                    {founderBlocks.length > 1 && (
                      <button onClick={() => removeFounderBlock(idx)}
                        className="text-[var(--cw-error)] text-xs inline-flex items-center gap-1"
                        data-testid={`remove-founder-${idx}`}>
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </div>
                  <FieldGroup
                    title=""
                    fields={data.founder_fields}
                    values={blk}
                    onChange={(k, v) => setFounderValue(idx, k, v)}
                    onClear={(k) => clearFounderValue(idx, k)}
                    testPrefix={`founder-${idx}`}
                    compact
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Stage selector (pre-deal only) */}
          {isPreDeal && (
            <div className="mt-6 cw-card p-5">
              <h3 className="cw-h3 mb-3">Initial {term("stage").toLowerCase()}</h3>
              <select value={stageId} onChange={(e) => setStageId(e.target.value)}
                className="w-full max-w-md h-9 px-3 rounded-md border text-sm bg-white"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="stage-select">
                {data.stages.map((s) => (
                  <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Submit */}
          <div className="mt-6 flex items-center justify-between sticky bottom-4 z-10">
            <div className="text-xs text-[var(--cw-muted)]">
              {requiredMissing.length > 0
                ? `${requiredMissing.length} required field(s) missing`
                : "All required fields set"}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={retry} disabled={retrying}
                className="cw-btn-ghost text-sm inline-flex items-center gap-1"
                data-testid="retry-btn">
                <RefreshCw size={12} className={retrying ? "animate-spin" : ""} /> Re-extract
              </button>
              <button onClick={submit}
                disabled={saving || requiredMissing.length > 0}
                className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="submit-mapping-btn">
                <CheckCircle2 size={14} />
                {isPreDeal
                  ? (saving ? `Creating ${term("deal").toLowerCase()}…` : `Create ${term("Deal")}`)
                  : (saving ? "Applying…" : "Apply to " + term("deal").toLowerCase())}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------- Field group renderer -------------------- */
function FieldGroup({ title, fields, values, onChange, onClear, testPrefix, compact }) {
  if (!fields || fields.length === 0) return null;
  const renderable = fields.filter((f) => f.field_type !== "computed");
  return (
    <div className={compact ? "" : "mt-6"}>
      {title && <h3 className="cw-h3 mb-3">{title}</h3>}
      <div className="grid sm:grid-cols-2 gap-4">
        {renderable.map((f) => {
          const key = f.is_custom ? f.field_definition_id : f.field_key;
          const v = values?.[key];
          return (
            <FieldRow
              key={key}
              field={f}
              value={v?.value ?? ""}
              meta={v?.meta}
              onChange={(val) => onChange(key, val)}
              onClear={() => onClear(key)}
              testId={`${testPrefix}-${key}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function FieldRow({ field, value, meta, onChange, onClear, testId }) {
  const t = field.field_type;
  const req = field.effective_required;
  const border = { borderColor: "var(--cw-border)" };

  let control;
  // CW6.2.7: force Source to use the shared 5-option dropdown even if
  // the backend ships it as a text field.
  if (field.field_key === "source") {
    control = (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 rounded-md border text-sm bg-white" style={border}
        data-testid={`input-${testId}`}>
        <option value="">—</option>
        {DEAL_SOURCE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  } else if (t === "dropdown" && Array.isArray(field.options)) {
    control = (
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 rounded-md border text-sm bg-white" style={border}
        data-testid={`input-${testId}`}>
        <option value="">—</option>
        {field.options.map((o) => (
          <option key={o.option_key} value={o.option_key}>{o.option_label || o.option_key}</option>
        ))}
      </select>
    );
  } else if (t === "textarea") {
    control = (
      <textarea value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full min-h-[80px] px-3 py-2 rounded-md border text-sm" style={border}
        data-testid={`input-${testId}`} />
    );
  } else if (t === "number" || t === "currency") {
    control = (
      <input type="text" inputMode="decimal" value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 rounded-md border text-sm" style={border}
        data-testid={`input-${testId}`} />
    );
  } else {
    control = (
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 rounded-md border text-sm" style={border}
        data-testid={`input-${testId}`} />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold">
          {field.effective_label}
          {req && <span className="text-[var(--cw-error)] ml-0.5">*</span>}
        </label>
        {meta && <SuggestionBadge meta={meta} onClear={onClear} />}
      </div>
      {control}
    </div>
  );
}

function SuggestionBadge({ meta, onClear }) {
  const color = meta.confidence === "high" ? "#1F6E3A"
              : meta.confidence === "medium" ? "#8A6510" : "#B02B2B";
  const bg =    meta.confidence === "high" ? "rgba(44,154,82,0.10)"
              : meta.confidence === "medium" ? "rgba(243,191,92,0.15)" : "rgba(214,69,69,0.10)";
  return (
    <span className="inline-flex items-center gap-1 text-[10px]"
      title={meta.source_ref || ""}>
      <span className="px-1.5 py-0.5 rounded-full" style={{ background: bg, color }}>
        AI · {meta.confidence}
      </span>
      {meta.source_type === "public" && meta.source_url && (
        <a href={meta.source_url} target="_blank" rel="noreferrer"
          className="text-[var(--cw-primary)] underline inline-flex items-center gap-0.5">
          source <ExternalLink size={9} />
        </a>
      )}
      <button onClick={onClear} className="text-[var(--cw-muted)] hover:text-[var(--cw-error)]"
        title="Discard suggestion">
        <Trash2 size={10} />
      </button>
    </span>
  );
}

function confidenceRank(c) {
  return c === "high" ? 3 : c === "medium" ? 2 : c === "low" ? 1 : 0;
}
